/* @flow */
import {fetchList} from 'nutshell-core/entities/fetch-list';
import {transformDataForReport} from 'nutshell-core/json-to-report-data';
import {safelyParseError} from 'nutshell-core/utils';
import type {ActionsObservable} from 'redux-observable';
import {Observable} from 'rxjs';
import {switchMap, filter, map, catchError} from 'rxjs/operators';

import {
    updateFormSubmissionsListData,
    failFormSubmissionsListData,
    updateFormEngagementReportChartData,
    failFormEngagementReportChartData,
    type FormEngagementReportDataRequestedAction,
    type FormSubmissionsListRequestedAction,
} from './forms-actions';
import {getEngagementReportPostParams, type FormEngagementReportPostParams} from './forms-utils';

export const requestFormSubmissionsListEpic = (action$: ActionsObservable<*>) =>
    action$
        .ofType('FORM_SUBMISSIONS_LIST_DATA_REQUESTED')
        .switchMap((action: FormSubmissionsListRequestedAction) => {
            const {filters, pageNum = 1, columns, sort, q} = action.payload;

            // Our pagination components aren't 0-based, but this specific
            // request expects page 0 to be the first page. Using Math.max() here
            // in case older URLs with page 0 are still in the wild
            const adjustedPageNum = Math.max(0, pageNum - 1);

            const listRequestStream = Observable.fromPromise(
                fetchList('contacts', {
                    filter: filters,
                    sort,
                    page: {page: adjustedPageNum},
                    fields: columns,
                    q,
                })
            );

            const formPeopleListDataStream = listRequestStream.map((response) =>
                updateFormSubmissionsListData(response)
            );

            return formPeopleListDataStream.catch((err) => {
                const safeError = safelyParseError(err);

                return Observable.of(failFormSubmissionsListData(safeError));
            });
        });

export const requestFormsReportDataEpic = (action$: ActionsObservable<*>) =>
    action$.ofType('FORM_ENGAGEMENT_REPORT_DATA_REQUESTED').pipe(
        switchMap((action: FormEngagementReportDataRequestedAction) => {
            const {reportParams, filters} = action.payload;
            const params = getEngagementReportPostParams(reportParams, filters);
            const requestStream = Observable.from(fetchData(params));
            const reportDataStream = requestStream.pipe(map(transformDataForReport));

            const reportChartDataStream = reportDataStream.pipe(
                map((data) => data.chartData),
                filter((chartData) => Boolean(chartData)),
                map((chartData) => updateFormEngagementReportChartData({chartData}))
            );

            return reportChartDataStream.pipe(
                catchError((err) => Observable.of(failFormEngagementReportChartData(err)))
            );
        })
    );

function fetchData(params: FormEngagementReportPostParams) {
    return Promise.all([fetchFormEngagementReportData(params)]);
}

function fetchFormEngagementReportData(params: FormEngagementReportPostParams): Promise<any> {
    const url = '/rest/forms/report';

    const ajaxParams = {
        url,
        dataType: 'json',
        data: params,
    };

    return ($.ajax(ajaxParams): any); // This is how we have to convince flow that the response is a ReportsResponse
}
